<template>
  <div class="auth-bg">
    <div class="auth-card">
      <div class="auth-logo">
        <img src="@/assets/solvea-logo-wordmark-blue.jpeg" alt="Solvea" />
      </div>
      <h1 class="auth-title">Research Portal</h1>

      <!--
        portal.py:portal_verify() redirects HERE (hardcoded "/login", not
        "/portal-login") after a reader clicks the verification-mail link -
        see verifyEmail()/VerifyView.vue, which can only trigger that
        redirect, never read where it lands. These banners are purely
        informational and never touch handleLogin()/login() below - a
        reader still has to go to the actual Reader-Portal login to sign in.
      -->
      <div v-if="showVerified" class="notice-box notice-success">
        <p>E-Mail bestätigt. Du kannst dich jetzt anmelden.</p>
      </div>
      <div v-else-if="showVerifyError" class="notice-box">
        <p>Dieser Bestätigungslink ist ungültig oder abgelaufen.</p>
        <form v-if="!verifyResent" @submit.prevent="handleVerifyResend" class="resend-form" novalidate>
          <input
            v-model="verifyResendEmail"
            type="email"
            class="resend-input"
            placeholder="you@example.com"
            autocomplete="email"
            required
          />
          <button type="submit" class="link-btn" :disabled="verifyResendLoading">
            {{ verifyResendLoading ? 'Sende…' : 'Neuen Link senden' }}
          </button>
        </form>
        <p v-else class="hint-text hint-success">{{ verifyResendMessage }}</p>
      </div>

      <form @submit.prevent="handleLogin" class="auth-form" novalidate>
        <div class="auth-field">
          <label class="auth-label" for="email">E-Mail</label>
          <div class="auth-input-wrap">
            <Mail class="auth-input-icon" :size="16" aria-hidden="true" />
            <input
              id="email"
              v-model="email"
              type="email"
              class="auth-input"
              placeholder="user@example.com"
              autocomplete="username"
              required
            />
          </div>
        </div>

        <div class="auth-field">
          <label class="auth-label" for="password">Passwort</label>
          <div class="auth-input-wrap">
            <Lock class="auth-input-icon" :size="16" aria-hidden="true" />
            <input
              id="password"
              v-model="password"
              type="password"
              class="auth-input"
              placeholder="••••••••"
              autocomplete="current-password"
              required
            />
          </div>
        </div>

        <button type="submit" class="auth-btn" :disabled="loading">
          {{ loading ? 'Anmelden…' : 'Anmelden' }}
        </button>

        <!--
          EMAIL_NOT_VERIFIED (only ever a reader/self-signup account, see
          auth.authenticate()) gets its own actionable block instead of the
          generic error - the password was correct, the account just isn't
          unlocked yet, so "resend the mail" is the real next step, not a
          dead-end error message.
        -->
        <div v-if="unverifiedEmail" class="notice-box">
          <p>Diese Adresse ist noch nicht bestätigt. Bitte prüfe dein Postfach.</p>
          <button type="button" class="link-btn" :disabled="unverifiedResendCooldown > 0" @click="handleUnverifiedResend">
            {{ unverifiedResendCooldown > 0 ? `Bestätigungsmail erneut senden (${unverifiedResendCooldown}s)` : 'Bestätigungsmail erneut senden' }}
          </button>
          <p v-if="unverifiedResendMessage" class="hint-text hint-success">{{ unverifiedResendMessage }}</p>
        </div>
        <div v-else-if="error" class="auth-alert" role="alert">{{ error }}</div>
      </form>

      <!--
        This is the page a fresh, unauthenticated visitor actually lands on
        (redirect: '/' -> '/expectations-framework' -> here, since that route
        has no area: 'portal'), so the Reader-Portal "Konto erstellen" entry
        point belongs here too, not only on /portal-login - otherwise a new
        reader with no account yet has no way to discover it. Deliberately a
        secondary link, not a second primary button: the internal login above
        stays the one primary action on this screen.
      -->
      <p class="auth-foot">
        Noch kein Konto?
        <router-link :to="{ name: 'register' }" class="auth-link">Konto erstellen</router-link>
      </p>
    </div>
    <LegalFooterLinks class="login-legal-footer" language="DE" variant="light" />
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { login, currentUser } from '@/services/auth'
import { portalLogin, resendVerification } from '@/services/portalApi'
import { Mail, Lock } from 'lucide-vue-next'
import LegalFooterLinks from '@/components/legal/LegalFooterLinks.vue'
import '@/assets/auth-pages.css'
import { isAreaMismatch } from '@/router/areaGuard'

const RESEND_COOLDOWN_SECONDS = 60

const router   = useRouter()
const route    = useRoute()
const email    = ref('')
const password = ref('')
const error    = ref('')
const loading  = ref(false)

const unverifiedEmail          = ref('')
const unverifiedResendCooldown = ref(0)
const unverifiedResendMessage  = ref('')
let unverifiedResendTimer      = null

// A `redirect` query value is never trusted blindly: it can be stale (e.g.
// left over from an unrelated earlier bounce - see the api.js comment on
// PUBLIC_AUTH_PATHS for exactly how that happened) or simply point at
// another public/login page. Landing back on a public route after a
// successful login is always wrong - you're not "not logged in" anymore -
// so any redirect target that resolves to a public route is ignored in
// favor of the fallback.
//
// Bug-Fund (Live-Betrieb nach Domainwechsel): dieselbe Funktion liess ein
// `redirect=/portal/...` auch für einen gerade frisch eingeloggten
// INTERNEN Nutzer (admin/pm/analyst) unverändert durch - App.vue wählt das
// Portal-Layout rein anhand von route.meta.area, ohne das erneut gegen die
// Rolle zu pruefen, und PortalLayout.vue filtert sein Menü nur nach
// page_keys (die ein Admin für "portal.*" nie hat) - Ergebnis: nur der
// ungefilterte "Konto"-Link sichtbar, alle internen Reiter weg. isExternal
// kommt vom gerade abgeschlossenen Login (login()/portalLogin() setzen
// currentUser synchron, siehe services/auth.js bzw. services/portalApi.js)
// - ein Redirect-Ziel, dessen Bereich nicht zur Rolle passt, wird jetzt
// verworfen, genau wie ein öffentliches Ziel oben.
function safeRedirect(target, fallback, isExternal) {
  if (typeof target !== 'string' || !target.startsWith('/')) return fallback
  const resolved = router.resolve(target)
  if (resolved.matched.length === 0 || resolved.meta?.public) return fallback
  if (isAreaMismatch({ isExternal, area: resolved.meta?.area, routeName: resolved.name })) {
    return fallback
  }
  return target
}

// A real response from auth_login()/portal_login() ALWAYS carries a `code`
// field, success or failure (see auth.py / app.py:auth_login - every error
// path returns {"error":..., "code":...}). Its absence means the response
// never actually reached that Flask handler at all - e.g. on the VPS before
// this Auftrag, Caddy had no route for /auth/login and let it fall through
// to the frontend container's own nginx, which replied with a plain 405 and
// no JSON body. `e.response?.data?.code` is then `undefined`, not a real
// error code - showing `e.message` in that case produced exactly the raw,
// meaningless "Request failed with status code 405" this Auftrag reported.
function isBackendUnreachable(e) {
  if (!e.response) return true // network error / CORS / no response at all
  if (e.response.status === 404 || e.response.status === 405) return true
  return typeof e.response.data?.code === 'undefined'
}

// One login form for both internal staff and Reader-Portal accounts (they
// used to be two separate pages/endpoints - /auth/login and
// /portal/auth/login - each of which flatly refuses the other account type
// with the SAME generic INVALID_CREDENTIALS response as a wrong password,
// by design, so the two can never be told apart from the response alone -
// see auth.authenticate()'s external_required check). So: try the internal
// endpoint first; only on an ambiguous INVALID_CREDENTIALS do we fall back
// to the portal endpoint to find out which one it actually was. Any other
// error (RATE_LIMITED, ACCOUNT_LOCKED, ACCOUNT_DISABLED) is specific to
// this email regardless of which endpoint answers, so there's no point
// retrying - show it immediately.
//
// Portal-Administration auf dem VPS: an unreachable /auth/login (see
// isBackendUnreachable() above - 404/405/network, not a real backend
// response) is treated exactly like an ambiguous INVALID_CREDENTIALS - fall
// back to /portal/auth/login instead of surfacing the raw transport error.
// This is what actually lets an admin log in on the VPS at all once
// /auth/login is Caddy-routed (see deploy/Caddyfile) - and degrades
// gracefully (generic message, not a stack of HTTP jargon) even if it were
// still misconfigured.
async function handleLogin() {
  error.value           = ''
  unverifiedEmail.value = ''
  loading.value = true

  try {
    await login(email.value, password.value)
    router.push(safeRedirect(route.query.redirect, '/', currentUser.value?.is_external))
    return
  } catch (e) {
    const code = e.response?.data?.code
    if (code === 'EMAIL_NOT_VERIFIED') {
      unverifiedEmail.value = email.value
      loading.value = false
      return
    }
    if (code !== 'INVALID_CREDENTIALS' && !isBackendUnreachable(e)) {
      error.value = e.response?.data?.error || 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.'
      loading.value = false
      return
    }
  }

  try {
    await portalLogin(email.value, password.value)
    router.push(safeRedirect(route.query.redirect, '/portal/dashboard', currentUser.value?.is_external))
  } catch (e) {
    const code = e.response?.data?.code
    if (code === 'EMAIL_NOT_VERIFIED') {
      unverifiedEmail.value = email.value
    } else if (code === 'INVALID_CREDENTIALS' || isBackendUnreachable(e)) {
      error.value = 'E-Mail oder Passwort ist falsch.'
    } else {
      error.value = e.response?.data?.error || 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.'
    }
  } finally {
    loading.value = false
  }
}

async function handleUnverifiedResend() {
  if (unverifiedResendCooldown.value > 0) return
  unverifiedResendMessage.value = ''
  try {
    const data = await resendVerification(unverifiedEmail.value)
    unverifiedResendMessage.value = data.message || 'Falls die Adresse verwendbar ist, wurde eine E-Mail versendet.'
  } catch {
    unverifiedResendMessage.value = 'Falls die Adresse verwendbar ist, wurde eine E-Mail versendet.'
  } finally {
    unverifiedResendCooldown.value = RESEND_COOLDOWN_SECONDS
    clearInterval(unverifiedResendTimer)
    unverifiedResendTimer = setInterval(() => {
      unverifiedResendCooldown.value -= 1
      if (unverifiedResendCooldown.value <= 0) clearInterval(unverifiedResendTimer)
    }, 1000)
  }
}

// verified=1 / verify_error=1 - portal.py:portal_verify() redirects here
// after a reader clicks the verification-mail link (see verifyEmail() /
// VerifyView.vue).
const showVerified   = computed(() => route.query.verified === '1')
const showVerifyError = computed(() => route.query.verify_error === '1')

const verifyResendEmail   = ref('')
const verifyResendLoading = ref(false)
const verifyResendMessage = ref('')
const verifyResent        = ref(false)

async function handleVerifyResend() {
  verifyResendLoading.value = true
  try {
    const data = await resendVerification(verifyResendEmail.value)
    verifyResendMessage.value = data.message || 'Falls die Adresse ein unverifiziertes Konto hat, wurde eine E-Mail versendet.'
  } catch {
    verifyResendMessage.value = 'Falls die Adresse ein unverifiziertes Konto hat, wurde eine E-Mail versendet.'
  } finally {
    verifyResent.value = true
    verifyResendLoading.value = false
  }
}

onUnmounted(() => clearInterval(unverifiedResendTimer))
</script>

<style scoped>
.notice-box {
  margin-bottom: 20px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
}
.notice-box.notice-success {
  background: #f0fdf4;
  border-color: #86efac;
  color: #15803d;
}
.notice-box p { margin: 0 0 8px; }
.link-btn {
  background: none;
  border: none;
  padding: 0;
  color: var(--solvea-blue-link);
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.link-btn:hover:not(:disabled) { text-decoration: underline; }
.link-btn:disabled { color: #9ca3af; cursor: not-allowed; }
.resend-form { display: flex; gap: 8px; align-items: center; }
.resend-input {
  flex: 1;
  min-width: 0;
  padding: 7px 10px;
  border: 1px solid var(--auth-border);
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
}
.hint-text { font-size: 12px; margin: 4px 0 0; }
.hint-success { color: #15803d; }
</style>
